import moment, { Moment } from 'moment'
import { isFactTimeDeviation } from 'components/plan/feedPlanDeviation'
import { IFeed, IFeedFact } from 'models/IFeed'
import { IGroupApexChart } from 'models/IGroup'
import { MetricBadge, MetricCardData, MetricPart } from './dayMetrics'
import { CleaningScope } from './cleaningMetrics'

export type TrendPoint = {
	date: string
	deliveredKg: number
	remainingKg: number
	heads: number
}

const value = (text: string): MetricPart => ({ text, tone: 'value' })
const unit = (text: string): MetricPart => ({ text, tone: 'unit' })
const muted = (text: string): MetricPart => ({ text, tone: 'muted' })

const formatInt = (amount: number) => Math.round(amount).toLocaleString('ru-RU')

const formatSigned = (amount: number) => {
	const rounded = Math.round(amount)
	const body = Math.abs(rounded).toLocaleString('ru-RU')
	if (rounded > 0) return `+${body}`
	if (rounded < 0) return `-${body}`
	return '0'
}

const formatDuration = (minutes: number): MetricPart[] => {
	const rounded = Math.round(minutes)
	const sign = rounded < 0 ? '-' : ''
	const abs = Math.abs(rounded)
	if (abs < 60) return [value(`${sign}${abs}`), unit('мин')]
	const hours = Math.floor(abs / 60)
	const rest = abs % 60
	if (!rest) return [value(`${sign}${hours}`), unit('ч')]
	return [value(`${sign}${hours}`), unit('ч'), value(String(rest)), unit('мин')]
}

const toMoment = (stamp?: number | null) => {
	if (stamp == null) return null
	return stamp < 1e12 ? moment.unix(stamp) : moment(stamp)
}

const weightKg = (fact: IFeedFact) => {
	const amount = fact.weight ?? fact.food_weight
	return typeof amount === 'number' && !Number.isNaN(amount) ? amount : 0
}

const planKg = (fact: IFeedFact) =>
	typeof fact.plan_weight === 'number' && !Number.isNaN(fact.plan_weight)
		? fact.plan_weight
		: null

const inScope = (groupId: number | undefined, scope: CleaningScope) =>
	groupId != null &&
	(scope.groupIds.length === 0 || scope.groupIds.includes(groupId))

const groupBadge = (title: string): MetricBadge => ({
	text: title,
	tone: 'neutral',
})

const alertBadge = (text: string): MetricBadge => ({
	text,
	tone: 'alert',
})

type WeightDeviation = {
	diff: number
	groupTitle: string
	date: string
}

type TimeDeviation = {
	minutes: number
	fact: Moment
	groupTitle: string
	date: string
}

export const buildDistributionCards = (
	days: { day: string; plans: IFeed[] }[],
	scope: CleaningScope,
	emptyMinutes: number | null,
): { cards: MetricCardData[]; trend: TrendPoint[] } => {
	let factSum = 0
	let planSum = 0
	let factCount = 0
	let planCount = 0
	let recorded = 0
	let onTime = 0
	let maxWeight: WeightDeviation | null = null
	let maxTime: TimeDeviation | null = null
	const trend: TrendPoint[] = []

	days.forEach(item => {
		let deliveredKg = 0
		let remainingKg = 0
		const heads = new Map<number, number>()

		item.plans.forEach(plan => {
			plan.feed_facts?.forEach(fact => {
				const groupId = fact.group?.id
				if (!inScope(groupId, scope) || groupId == null) return
				const title = fact.group?.title || ''

				if (plan.type === 'cleaning' && fact.fact_dt) {
					remainingKg += weightKg(fact)
				}
				if (plan.type !== 'distribution') return

				const planned = planKg(fact)
				if (planned != null) {
					planSum += planned
					planCount += 1
				}
				if (!fact.fact_dt) return

				const actual = weightKg(fact)
				factSum += actual
				factCount += 1
				deliveredKg += actual
				recorded += 1
				if (fact.group?.cattles_qty) {
					heads.set(groupId, fact.group.cattles_qty)
				}
				if (
					!isFactTimeDeviation(
						fact.fact_dt,
						fact.plan_time || plan.time,
						item.day,
						plan.deviation,
					)
				) {
					onTime += 1
				}
				if (planned != null) {
					const diff = actual - planned
					if (!maxWeight || diff > maxWeight.diff) {
						maxWeight = { diff, groupTitle: title, date: item.day }
					}
				}
				const factMoment = toMoment(fact.fact_dt)
				const planTime = fact.plan_time || plan.time
				if (factMoment && planTime) {
					const [hours, minutes] = planTime.slice(0, 5).split(':').map(Number)
					if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
						const planMoment = moment(item.day).set({
							hour: hours,
							minute: minutes,
							second: 0,
							millisecond: 0,
						})
						const diffMinutes = factMoment.diff(planMoment, 'minutes', true)
						if (!maxTime || diffMinutes > maxTime.minutes) {
							maxTime = {
								minutes: diffMinutes,
								fact: factMoment,
								groupTitle: title,
								date: item.day,
							}
						}
					}
				}
			})
		})

		if (deliveredKg > 0 || remainingKg > 0) {
			trend.push({
				date: item.day,
				deliveredKg,
				remainingKg,
				heads: Array.from(heads.values()).reduce((sum, qty) => sum + qty, 0),
			})
		}
	})

	const compliance = recorded > 0 ? (onTime / recorded) * 100 : null
	const late = compliance == null ? null : 100 - compliance
	const showDetails = scope.period || scope.multipleGroups
	const duration = emptyMinutes == null ? null : formatDuration(emptyMinutes)

	const cards: MetricCardData[] = [
		{
			id: 'distributed-total',
			parts: [
				value(formatInt(factSum)),
				unit('кг'),
				muted(`/ ${formatInt(planSum)} кг`),
			],
			label: 'Роздано корма всего',
			labelNote: '(факт/план)',
			empty: factCount === 0 && planCount === 0,
		},
		{
			id: 'weight-deviation',
			parts: [
				value(maxWeight ? formatSigned(maxWeight.diff) : '0'),
				unit('кг'),
			],
			label: 'Максимальное отклонение от планового веса раздачи',
			empty: !maxWeight,
			badge:
				scope.multipleGroups && maxWeight
					? groupBadge(maxWeight.groupTitle)
					: undefined,
			aside:
				scope.period && maxWeight
					? `Было ${moment(maxWeight.date).format('DD.MM.YY')}`
					: undefined,
		},
		{
			id: 'distributed-avg',
			parts: [
				value(factCount ? formatInt(factSum / factCount) : '0'),
				unit('кг'),
				muted(`/ ${planCount ? formatInt(planSum / planCount) : '0'} кг`),
			],
			label: 'Средний вес раздан за день',
			labelNote: '(факт/план)',
			empty: factCount === 0 && planCount === 0,
		},
		{
			id: 'distribution-time',
			parts: [
				value(compliance == null ? '0' : formatInt(compliance)),
				unit('%'),
			],
			label: 'Соблюдение времени раздачи',
			empty: compliance == null,
			badge:
				late == null ? undefined : alertBadge(`${formatInt(late)}% опозданий`),
		},
	]

	if (showDetails) {
		cards.push({
			id: 'distribution-deviation',
			parts: maxTime
				? formatDuration(maxTime.minutes)
				: [value('0'), unit('мин')],
			label: 'Максимальное отклонение от времени раздачи',
			empty: !maxTime,
			badge:
				scope.multipleGroups && maxTime
					? groupBadge(maxTime.groupTitle)
					: undefined,
			aside: maxTime ? `было ${maxTime.fact.format('HH:mm:ss')}` : undefined,
		})
	}

	cards.push({
		id: 'empty-table',
		parts: duration ?? [value('0'), unit('мин')],
		label: 'Кормовой стол пустой',
		empty: emptyMinutes == null,
	})

	return { cards, trend }
}

export const buildPushesCard = (
	days: { day: string; plans: IFeed[] }[],
	scope: CleaningScope,
): MetricCardData => {
	let planned = 0
	let done = 0
	let quality = 0

	days.forEach(item => {
		item.plans.forEach(plan => {
			if (plan.type !== 'nudge') return
			plan.feed_facts?.forEach(fact => {
				if (!inScope(fact.group?.id, scope)) return
				planned += 1
				if (!fact.fact_dt) return
				done += 1
				if (
					!isFactTimeDeviation(
						fact.fact_dt,
						fact.plan_time || plan.time,
						item.day,
						plan.deviation,
					)
				) {
					quality += 1
				}
			})
		})
	})

	const donePercent = planned > 0 ? (done / planned) * 100 : null
	const qualityPercent = done > 0 ? (quality / done) * 100 : null

	return {
		id: 'nudge-schedule',
		parts: [
			value(donePercent == null ? '0' : formatInt(donePercent)),
			unit('%'),
		],
		label: 'Выполнено подталкиваний по расписанию',
		empty: donePercent == null,
		badge:
			qualityPercent == null
				? undefined
				: alertBadge(`${formatInt(qualityPercent)}% качественно`),
	}
}

export const emptyMinutesFromGraphs = (series: IGroupApexChart[][]) => {
	if (series.length === 0) return null
	let minutes = 0
	let hasPoint = false

	series.forEach(points => {
		const sorted = points
			.filter(point => point.start_dt != null && point.t_feed != null)
			.slice()
			.sort((left, right) => left.start_dt - right.start_dt)
		if (sorted.length > 1) hasPoint = true
		for (let index = 0; index < sorted.length - 1; index += 1) {
			if (sorted[index].t_feed >= 0.2) continue
			const start = toMoment(sorted[index].start_dt)
			const end = toMoment(sorted[index + 1].start_dt)
			if (!start || !end || !end.isAfter(start)) continue
			minutes += end.diff(start, 'minutes', true)
		}
	})

	return hasPoint ? minutes : null
}
